import { useCallback, useRef, useState } from 'react'

interface ScrubberProps {
  currentTime: number
  duration: number
  buffered?: number
  onSeek: (seconds: number) => void
  label?: string
  /** Thinner track, no hover tooltip — for the docked player. */
  compact?: boolean
}

/**
 * A seek bar that stays smooth while dragging: local state drives the thumb
 * during a drag so it never fights the `timeupdate` events still arriving from
 * the audio element, and the real seek is committed on release.
 */
export function Scrubber({
  currentTime,
  duration,
  buffered = 0,
  onSeek,
  label = 'Seek',
  compact = false,
}: ScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragTime, setDragTime] = useState<number | null>(null)
  const [hoverTime, setHoverTime] = useState<number | null>(null)

  const safeDuration = duration > 0 ? duration : 0
  const displayed = dragTime ?? currentTime
  const percent = safeDuration ? Math.min(100, (displayed / safeDuration) * 100) : 0
  const bufferedPercent = safeDuration ? Math.min(100, (buffered / safeDuration) * 100) : 0

  const timeFromEvent = useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect()
      if (!rect || !safeDuration) return 0
      const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1)
      return ratio * safeDuration
    },
    [safeDuration],
  )

  const beginDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!safeDuration) return
      event.currentTarget.setPointerCapture(event.pointerId)
      setDragTime(timeFromEvent(event.clientX))
    },
    [safeDuration, timeFromEvent],
  )

  const moveDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (safeDuration) setHoverTime(timeFromEvent(event.clientX))
      if (dragTime === null) return
      setDragTime(timeFromEvent(event.clientX))
    },
    [dragTime, safeDuration, timeFromEvent],
  )

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragTime === null) return
      event.currentTarget.releasePointerCapture(event.pointerId)
      onSeek(dragTime)
      setDragTime(null)
    },
    [dragTime, onSeek],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!safeDuration) return
      const jump = event.shiftKey ? 30 : 5
      const actions: Record<string, number | undefined> = {
        ArrowRight: currentTime + jump,
        ArrowLeft: currentTime - jump,
        Home: 0,
        End: safeDuration,
        PageUp: currentTime + 60,
        PageDown: currentTime - 60,
      }
      const next = actions[event.key]
      if (next === undefined) return
      event.preventDefault()
      onSeek(Math.min(Math.max(next, 0), safeDuration))
    },
    [currentTime, safeDuration, onSeek],
  )

  return (
    <div
      ref={trackRef}
      className={`scrubber${compact ? ' scrubber-compact' : ''}${dragTime !== null ? ' is-dragging' : ''}`}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={Math.round(safeDuration)}
      aria-valuenow={Math.round(displayed)}
      aria-valuetext={`${Math.floor(displayed / 60)} minutes ${Math.floor(displayed % 60)} seconds`}
      onPointerDown={beginDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => setHoverTime(null)}
      onKeyDown={onKeyDown}
    >
      <div className="scrubber-track">
        <div className="scrubber-buffered" style={{ width: `${bufferedPercent}%` }} />
        <div className="scrubber-progress" style={{ width: `${percent}%` }} />
        <div className="scrubber-thumb" style={{ left: `${percent}%` }} />
        {hoverTime !== null && safeDuration > 0 && (
          <span
            className="scrubber-tooltip"
            style={{ left: `${(hoverTime / safeDuration) * 100}%` }}
            aria-hidden="true"
          >
            {Math.floor(hoverTime / 60)}:{String(Math.floor(hoverTime % 60)).padStart(2, '0')}
          </span>
        )}
      </div>
    </div>
  )
}
